import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Printer } from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/i18n';
import { CHECK_TRANSITIONS, PAYMENT_TRANSITIONS, canTransition } from '@/lib/domain';
import { ChargeType, CheckStatus, PaymentStatus } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import SplitDialog from './SplitDialog';
import PrintableCheck from './PrintableCheck';
import DiscountDialog from './DiscountDialog';
import { RefundDialog, TakePaymentDialog, TipDialog } from './PaymentDialogs';
import {
  type CashierShift,
  type Charge,
  type Check,
  type Payment,
  apiError,
  balanceOf,
  toMinor,
} from './lib';

/**
 * D-07 — `ChecksService.MUTABLE_STATUSES`. `loadMutable()` throws
 * `409 Check N is PAID and cannot be changed` from anything else, and it sits
 * under split, merge, move-charges, discount and charge-void. The screen used
 * to ask none of this: on a PAID, CLOSED or VOIDED check every button was live
 * and every press was a 409.
 */
const MUTABLE_STATUSES: CheckStatus[] = [CheckStatus.OPEN, CheckStatus.PARTIALLY_PAID];

const isMutable = (status: CheckStatus): boolean => MUTABLE_STATUSES.includes(status);

/**
 * A refund is a payment-level move, not a check-level one: `applyRefund` asserts
 * `PAYMENT_TRANSITIONS` on the payment's own status, so a PENDING authorization
 * or an already fully REFUNDED payment refuses one however the check looks.
 */
const isRefundable = (status: string): boolean =>
  canTransition(PAYMENT_TRANSITIONS, status as PaymentStatus, PaymentStatus.REFUNDED) ||
  canTransition(PAYMENT_TRANSITIONS, status as PaymentStatus, PaymentStatus.PARTIALLY_REFUNDED);

interface Props {
  checkId: string;
  checks: Check[];
  shift: CashierShift | null;
  onChanged: () => void;
  /**
   * Passed straight to `TakePaymentDialog` — see its own note. A screen that IS
   * a cash drawer (`Billing/Register.tsx`) refuses a cash payment with no shift
   * open, because the API books that to `shiftId: null` and the money lands
   * where the count cannot find it. Default off, so the two call sites that
   * mounted this before are unchanged.
   */
  cashRequiresShift?: boolean;
}

/**
 * One bill, everywhere a bill is settled.
 *
 * ## This takes props, and that is load-bearing
 *
 * It is mounted at **two** call sites — `Billing/Checks.tsx` (a dialog over the
 * till's list) and `Spaces/components/VisitPanes.tsx` (the floor map's Bill
 * pane, reached from `TableSessionDialog`). `checks` and `shift` come from
 * whichever parent that is: the till has the day's list and the open drawer,
 * the floor map has the visit's own checks and the same drawer. Turning this into a `/checks/:id` route would give the floor map
 * nothing to hand it — a route cannot supply a parent's state — so the design's
 * routed page is built as this component's *layout*, not its *address*. The
 * props signature is deliberately unchanged.
 *
 * ## The payments region stays
 *
 * The Check Detail frame has no payments region at all. It draws `Close & pay`
 * as one button and then offers no way to take the payment, so a check that owes
 * anything could never reach PAID — and PAID is the only state `CLOSED` is
 * reachable from. The API has four payment mutations (create, refund, void,
 * tip); three of them are wired here and the frame exposes none. So the frame's
 * right rail is added *beside* the payments table, never instead of it.
 *
 * ## The totals block has no percentages, on purpose
 *
 * The frame labels `Service charge (10%)` and `Tax (15%)`. There is no rate on a
 * check to read either from: `serviceTotal` is the sum of `ChargeType.FEE`
 * charges and `taxTotal` is the sum of each charge's own `taxAmount`. Tax here
 * is per-charge data, never a rate applied to a base, and nothing in the product
 * auto-applies a service charge. The amounts are real and stored; the
 * percentages would be invented, so the labels are plain.
 *
 * `tipTotal` and `paidTotal` are kept even though the frame drops both. The
 * bill is the bill — `grandTotal` excludes the tip — but money received is
 * `paidTotal + tipTotal`, which is what the drawer's `expectedCash` counts. A
 * Check Detail that cannot show the tip cannot be reconciled against the till.
 *
 * ## What is drawn and cannot be
 *
 * `Void` at check level has no route — `checks.controller.ts` has none, and
 * `VOIDED` is only ever written as the source side of a merge. Charge-level void
 * is what exists and is what the rows offer. `Assigned Server` has no field on
 * `Check` at all. `DISPATCH LOGS` matches nothing in either repo. The frame's
 * `Unpaid` pill is not a member of `CheckStatus` or `PaymentStatus` and is never
 * handed to `toneForStatus`; the real status and the real balance say the same
 * thing truthfully.
 */
export default function CheckDetail({
  checkId,
  checks,
  shift,
  onChanged,
  cashRequiresShift = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const [check, setCheck] = useState<Check | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState('');
  const [splitOpen, setSplitOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [refunding, setRefunding] = useState<Payment | null>(null);
  const [tipping, setTipping] = useState<Payment | null>(null);
  const [voiding, setVoiding] = useState<Charge | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [printing, setPrinting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<Check>(`/checks/${checkId}`)
      .then(({ data }) => setCheck(data))
      .catch((e) => setError(apiError(e, t('billing.checks.loadFailed'))))
      .finally(() => setLoading(false));
  }, [checkId, t]);

  useEffect(() => {
    setSelected([]);
    load();
  }, [load]);

  const refresh = () => {
    load();
    onChanged();
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!check) return <EmptyState title={t('billing.checks.notFound')} />;

  const money = (value: string) => formatCurrency(value, check.currency, i18n.language);
  const outstanding = balanceOf(check);
  const others = checks.filter((c) => c.id !== check.id);
  // Both sides of a merge and both sides of a move-charges must be mutable —
  // the service calls `loadMutable` on each — so a settled check is not an
  // option a cashier can pick and then be refused.
  const openOthers = others.filter((other) => isMutable(other.status));

  const mutable = isMutable(check.status);
  // Only PAID → CLOSED exists in the machine. Close on an unpaid OPEN check —
  // the commonest state there is, and the one still answering "owes 87.50" —
  // was a guaranteed 409.
  const closable = canTransition(CHECK_TRANSITIONS, check.status, CheckStatus.CLOSED);

  /** Every credit already given away on this bill — the frame's Applied Offers. */
  const discounts = (check.charges ?? []).filter((charge) => charge.type === ChargeType.DISCOUNT);

  /**
   * Sibling bills on the same visit, out of what the parent handed down. Stated
   * only when there is more than one, because that is the half of it this can
   * know: the till's list is filtered to a day and a status, so an absence of
   * siblings here is not evidence the bill was never split. The frame's
   * `Single check` claims exactly that evidence, and `Check` has no
   * `splitStatus` field to get it from.
   */
  const siblings = check.sessionId
    ? checks.filter((c) => c.sessionId === check.sessionId).length
    : 0;

  const moveCharges = async () => {
    try {
      await api.post(`/checks/${check.id}/move-charges`, {
        targetCheckId: moveTarget,
        chargeIds: selected,
      });
      toast.success(t('billing.move.done'));
      setMoveOpen(false);
      setSelected([]);
      refresh();
    } catch (e) {
      toast.error(apiError(e, t('billing.move.failed')));
    }
  };

  const merge = async () => {
    try {
      await api.post(`/checks/${check.id}/merge`, { sourceCheckId: mergeSource });
      toast.success(t('billing.merge.done'));
      setMergeOpen(false);
      refresh();
    } catch (e) {
      toast.error(apiError(e, t('billing.merge.failed')));
    }
  };

  const closeCheck = async () => {
    try {
      await api.post(`/checks/${check.id}/close`, {});
      toast.success(t('billing.checks.closed'));
      refresh();
    } catch (e) {
      // A negative `grandTotal` is refused with 409 naming the amount and
      // pointing at void/refund. The message is the server's and is shown as
      // it is — nothing here floors the total, because flooring it to 0.00 is
      // what erases the evidence that the refusal exists to keep.
      toast.error(apiError(e, t('billing.checks.closeFailed')));
    }
  };

  const voidCharge = async () => {
    if (!voiding) return;
    try {
      await api.post(`/charges/${voiding.id}/void`, { reason: voidReason });
      toast.success(t('billing.charges.voided'));
      setVoiding(null);
      setVoidReason('');
      refresh();
    } catch (e) {
      toast.error(apiError(e, t('billing.charges.voidFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              {t('billing.checks.checkNumber', { number: check.number })}
              <Badge tone={toneForStatus(check.status)}>{t(`billing.status.${check.status}`)}</Badge>
              {check.isMaster && <Badge tone="brand">{t('billing.checks.master')}</Badge>}
            </CardTitle>
            <p className="text-xs text-ink-muted">
              {check.session
                ? check.session.table
                  ? t('billing.checks.tableLine', { number: check.session.table.number })
                  : t('billing.checks.visitLine')
                : t('billing.checks.walkIn')}{' '}
              · {formatDateTime(check.openedAt, i18n.language)}
            </p>
          </div>

          <div className="flex flex-col items-end gap-1.5">
            <div className="flex flex-wrap gap-2">
              {/* A bill prints in any status — a settled check is exactly the
                  one a guest asks for a copy of. Same key the read needed. */}
              {can('checks.view') && (
                <Button size="sm" variant="outline" onClick={() => setPrinting(true)}>
                  <Icon as={Printer} />
                  {t('billing.print.print')}
                </Button>
              )}
              {/* Its own permission key, separate from `checks.update`. */}
              {can('discounts.apply') && mutable && (
                <Button size="sm" variant="outline" onClick={() => setDiscountOpen(true)}>
                  {t('billing.discount.apply')}
                </Button>
              )}
              {can('checks.split') && mutable && (
                <Button size="sm" variant="outline" onClick={() => setSplitOpen(true)}>
                  {t('billing.actions.split')}
                </Button>
              )}
              {can('checks.merge') && mutable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMergeOpen(true)}
                  disabled={openOthers.length === 0}
                >
                  {t('billing.actions.merge')}
                </Button>
              )}
              {can('payments.create') && mutable && (
                <Button size="sm" onClick={() => setPayOpen(true)}>
                  {t('billing.actions.takePayment')}
                </Button>
              )}
              {can('checks.close') && closable && (
                <Button size="sm" variant="secondary" onClick={closeCheck}>
                  {t('billing.actions.close')}
                </Button>
              )}
            </div>
            {/* Say why the buttons are gone, rather than showing a bare gap. */}
            {!mutable && (
              <p className="text-xs text-ink-muted">
                {t('billing.checks.settled', {
                  status: t(`billing.status.${check.status}`),
                })}
              </p>
            )}
            {!closable && mutable && can('checks.close') && (
              <p className="text-xs text-ink-muted">{t('billing.checks.closeNeedsPaid')}</p>
            )}
          </div>
        </CardHeader>
      </Card>

      {/*
          `min-w-0` on both columns, not just the track.

          A grid item's `min-width` is `auto`, which floors it at its content's
          min-content width — and the charges table's min-content width is wider
          than 320px however hard `TableWrap` tries to scroll inside itself. The
          `minmax(0,1fr)` on the track only covers the xl case; below xl this is
          a one-column grid and the item is still floored. Without it the whole
          page scrolls sideways on a phone instead of the table doing it.
      */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>{t('billing.charges.title')}</CardTitle>
              {can('checks.move_charge') && mutable && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selected.length === 0 || openOthers.length === 0}
                  onClick={() => setMoveOpen(true)}
                >
                  {t('billing.actions.moveSelected', { count: selected.length })}
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {(check.charges ?? []).length === 0 ? (
                <EmptyState title={t('billing.charges.empty')} />
              ) : (
                <TableWrap className="rounded-none border-x-0 border-t-0">
                  <Table>
                    <THead>
                      <tr>
                        <TH className="w-8" />
                        <TH>{t('billing.charges.description')}</TH>
                        <TH>{t('common.quantity')}</TH>
                        <TH>{t('billing.charges.amount')}</TH>
                        <TH className="text-end">{t('catalog.items.actions')}</TH>
                      </tr>
                    </THead>
                    <tbody>
                      {check.charges?.map((charge) => (
                        <TR key={charge.id} className={charge.voidedAt ? 'opacity-60' : ''}>
                          <TD>
                            <input
                              type="checkbox"
                              disabled={!!charge.voidedAt}
                              checked={selected.includes(charge.id)}
                              onChange={(e) =>
                                setSelected(
                                  e.target.checked
                                    ? [...selected, charge.id]
                                    : selected.filter((id) => id !== charge.id),
                                )
                              }
                              aria-label={charge.description}
                            />
                          </TD>
                          <TD>
                            <div className="font-medium">{charge.description}</div>
                            <div className="flex flex-wrap gap-1 text-xs">
                              <Badge tone="neutral">{t(`billing.chargeType.${charge.type}`)}</Badge>
                              {charge.voidedAt && (
                                <Badge tone="danger">
                                  {t('billing.charges.voidedWith', {
                                    reason: charge.voidReason ?? '—',
                                  })}
                                </Badge>
                              )}
                            </div>
                          </TD>
                          <TD>{charge.quantity}</TD>
                          <TD>
                            {money(charge.amount)}
                            {toMinor(charge.taxAmount, check.currency) > 0 && (
                              <div className="text-xs text-ink-muted">
                                {t('billing.charges.plusTax', { amount: money(charge.taxAmount) })}
                              </div>
                            )}
                          </TD>
                          <TD className="text-end">
                            {!charge.voidedAt && can('checks.update') && mutable && (
                              <Button size="sm" variant="danger" onClick={() => setVoiding(charge)}>
                                {t('billing.actions.void')}
                              </Button>
                            )}
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}

              {/* The frame's totals block: right-aligned under the lines, with
                  the balance owed as the emphasised figure rather than a bare
                  `Total`, because the balance is what the cashier acts on. */}
              <dl className="ms-auto max-w-xs space-y-1 p-5 text-sm">
                <Row label={t('common.subtotal')} value={money(check.subtotal)} />
                <Row label={t('common.discount')} value={money(check.discountTotal)} />
                <Row label={t('billing.checks.service')} value={money(check.serviceTotal)} />
                <Row label={t('common.tax')} value={money(check.taxTotal)} />
                <Row label={t('common.tip')} value={money(check.tipTotal)} />
                <Row label={t('billing.checks.paid')} value={money(check.paidTotal)} />
                <div className="flex items-center justify-between gap-2 border-t pt-2 text-base font-semibold">
                  <dt>{t('billing.checks.outstanding')}</dt>
                  <dd
                    className={
                      toMinor(outstanding, check.currency) > 0
                        ? 'text-amber-700 dark:text-amber-400'
                        : 'text-green-700 dark:text-green-400'
                    }
                  >
                    {money(outstanding)}
                    <span className="ms-2 text-xs font-normal text-ink-muted">
                      {t('billing.checks.ofTotal', { total: money(check.grandTotal) })}
                    </span>
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('billing.payments.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(check.payments ?? []).length === 0 ? (
                <EmptyState
                  title={t('billing.payments.empty')}
                  hint={t('billing.payments.emptyHint')}
                />
              ) : (
                <TableWrap className="rounded-none border-x-0 border-t-0">
                  <Table>
                    <THead>
                      <tr>
                        <TH>{t('billing.payment.method')}</TH>
                        <TH>{t('billing.payment.amount')}</TH>
                        <TH>{t('common.tip')}</TH>
                        <TH>{t('billing.payments.refunded')}</TH>
                        <TH>{t('billing.payments.status')}</TH>
                        <TH className="text-end">{t('catalog.items.actions')}</TH>
                      </tr>
                    </THead>
                    <tbody>
                      {check.payments?.map((payment) => (
                        <TR key={payment.id}>
                          <TD>
                            {t(`billing.method.${payment.method}`)}
                            {payment.reference && (
                              <div className="text-xs text-ink-muted">{payment.reference}</div>
                            )}
                          </TD>
                          <TD>
                            {formatCurrency(payment.amount, payment.currency, i18n.language)}
                          </TD>
                          <TD>
                            {formatCurrency(payment.tipAmount, payment.currency, i18n.language)}
                          </TD>
                          <TD>
                            {formatCurrency(payment.refundedAmount, payment.currency, i18n.language)}
                          </TD>
                          <TD>
                            <Badge tone={toneForStatus(payment.status)}>
                              {t(`billing.paymentStatus.${payment.status}`, payment.status)}
                            </Badge>
                          </TD>
                          <TD className="text-end">
                            <div className="flex flex-wrap justify-end gap-2">
                              {can('payments.create') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setTipping(payment)}
                                >
                                  {t('billing.actions.addTip')}
                                </Button>
                              )}
                              {can('payments.refund') && isRefundable(payment.status) && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => setRefunding(payment)}
                                >
                                  {t('billing.actions.refund')}
                                </Button>
                              )}
                            </div>
                          </TD>
                        </TR>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('billing.payments.detailsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-1 text-sm">
                <Row
                  label={t('billing.payments.status')}
                  value={t(`billing.status.${check.status}`)}
                />
                <Row label={t('billing.checks.paid')} value={money(check.paidTotal)} />
                <Row label={t('billing.checks.outstanding')} value={money(outstanding)} />
                {siblings > 1 && (
                  <Row
                    label={t('billing.checks.onThisVisit')}
                    value={String(siblings)}
                  />
                )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>
                {t('billing.discount.appliedTitle')}
              </CardTitle>
              {can('discounts.apply') && mutable && (
                <Button size="sm" variant="ghost" onClick={() => setDiscountOpen(true)}>
                  {t('common.add')}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {discounts.length === 0 ? (
                <p className="text-sm text-ink-muted">
                  {t('billing.discount.none')}
                </p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {discounts.map((charge) => (
                    <li key={charge.id} className="flex items-start justify-between gap-2">
                      <span className={charge.voidedAt ? 'text-ink-muted line-through' : ''}>
                        {charge.description}
                        {/* A discount parked for approval is written as a VOIDED
                            charge so the bill records what was asked. It is not
                            money off until someone signs it. */}
                        {charge.voidedAt && (
                          <span className="block text-xs no-underline">
                            {t('billing.discount.notApplied', {
                              reason: charge.voidReason ?? '—',
                            })}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 tabular-nums">{money(charge.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {t('billing.checks.linkedTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-1 text-sm">
                <Row
                  label={t('billing.checks.linkedVisit')}
                  value={
                    check.session
                      ? check.session.table
                        ? t('billing.checks.tableLine', { number: check.session.table.number })
                        : t('billing.checks.visitLine')
                      : t('billing.checks.walkIn')
                  }
                />
                <Row
                  label={t('billing.checks.guests')}
                  value={
                    check.session?.guestCount !== undefined
                      ? String(check.session.guestCount)
                      : '—'
                  }
                />
                <Row
                  label={t('billing.checks.openedAt')}
                  value={formatDateTime(check.openedAt, i18n.language)}
                />
              </dl>
              {/*
                The frame also links an order. `GET /checks/:id` includes the
                `session` (with its table), `charges` and `payments` — `orders`
                is a relation but is not in the `findOne` include, so it would be
                a second call on a second permission and is not invented here.
                The table IS reachable now, through the session, which is the
                whole of what replaced `Check.partyId`.
              */}
            </CardContent>
          </Card>
        </div>
      </div>

      {moveOpen && (
        <Dialog
          open
          onClose={() => setMoveOpen(false)}
          title={t('billing.move.title')}
          description={t('billing.move.subtitle', { count: selected.length })}
          footer={
            <>
              <Button variant="outline" onClick={() => setMoveOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={moveCharges} disabled={!moveTarget}>
                {t('billing.actions.move')}
              </Button>
            </>
          }
        >
          <Field label={t('billing.move.target')} hint={t('billing.move.targetHint')}>
            <Select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}>
              <option value="">{t('billing.move.chooseCheck')}</option>
              {openOthers.map((other) => (
                <option key={other.id} value={other.id}>
                  {t('billing.checks.checkNumber', { number: other.number })}
                  {other.isMaster ? ` · ${t('billing.checks.master')}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        </Dialog>
      )}

      {mergeOpen && (
        <Dialog
          open
          onClose={() => setMergeOpen(false)}
          title={t('billing.merge.title', { number: check.number })}
          description={t('billing.merge.subtitle')}
          footer={
            <>
              <Button variant="outline" onClick={() => setMergeOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={merge} disabled={!mergeSource}>
                {t('billing.actions.merge')}
              </Button>
            </>
          }
        >
          <Field label={t('billing.merge.source')}>
            <Select value={mergeSource} onChange={(e) => setMergeSource(e.target.value)}>
              <option value="">{t('billing.move.chooseCheck')}</option>
              {openOthers.map((other) => (
                <option key={other.id} value={other.id}>
                  {t('billing.checks.checkNumber', { number: other.number })}
                </option>
              ))}
            </Select>
          </Field>
        </Dialog>
      )}

      {voiding && (
        <Dialog
          open
          onClose={() => setVoiding(null)}
          title={t('billing.charges.voidTitle')}
          description={t('billing.charges.voidHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setVoiding(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={voidCharge} disabled={!voidReason.trim()}>
                {t('billing.actions.void')}
              </Button>
            </>
          }
        >
          <Field label={t('billing.charges.voidReason')} required>
            <Textarea rows={3} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
          </Field>
        </Dialog>
      )}

      {splitOpen && (
        <SplitDialog check={check} onClose={() => setSplitOpen(false)} onDone={refresh} />
      )}
      {discountOpen && (
        <DiscountDialog check={check} onClose={() => setDiscountOpen(false)} onDone={refresh} />
      )}
      {payOpen && (
        <TakePaymentDialog
          check={check}
          shift={shift}
          cashRequiresShift={cashRequiresShift}
          onClose={() => setPayOpen(false)}
          onDone={refresh}
        />
      )}
      {refunding && (
        <RefundDialog payment={refunding} onClose={() => setRefunding(null)} onDone={refresh} />
      )}
      {tipping && <TipDialog payment={tipping} onClose={() => setTipping(null)} onDone={refresh} />}
      {printing && <PrintableCheck check={check} onClose={() => setPrinting(false)} />}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-end">{value}</dd>
    </div>
  );
}
